/**
 * Reusable agency-side share-link flow.
 *
 * Backs the Market and Placements "Prepare to share" UX: pick a few
 * listings, generate a public buyer page (`/share/:token`) with the
 * broker's contact details on every card, then copy / revoke the link.
 *
 * The backend (`app/routes/share_links.py` + `app/services/client_share.py`)
 * already accepts both `kind: "placement"` and `kind: "hot_offer"`
 * selections and rehosts photos in a background daemon — so everything
 * here is presentation + a thin `/api/share-links` client.
 *
 * Two entry points share the one dialog: "Prepare to share" (pick listings
 * on the page) and "From URLs" (paste MyHome / ss.ge links you already
 * have). Both post to `POST /api/share-links`, which takes `selections`
 * and `urls` together — so the resulting public page is the same either
 * way, and a link can mix both.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/api/client";
import type {
  ClientShareLink,
  ClientShareLinkDetail,
  ShareItemSnapshot,
} from "@/types";

export type ShareLang = "ka" | "ru" | "en";

/** Public-page languages offered in the prepare dialog. */
export const SHARE_LANGS: { code: ShareLang; label: string; flag: string }[] = [
  { code: "ka", label: "GE", flag: "🇬🇪" },
  { code: "ru", label: "RU", flag: "🇷🇺" },
  { code: "en", label: "EN", flag: "🇬🇧" },
];

/** Backend cap — keep in lockstep with `share_links._ShareCreateIn`. */
/** Cards per share link. Must match ``MAX_SHARE_ITEMS`` in
 *  app/routes/share_links.py — the API rejects anything above it. */
export const SHARE_MAX_SELECTIONS = 20;

export type ShareSelection = {
  kind: "placement" | "hot_offer" | "fresh";
  id: string;
};

/** Public-page lifetimes (days) offered in the prepare dialog — matches
 *  `ALLOWED_LIFETIMES` in `app/routes/share_links.py`. */
export const SHARE_LIFETIMES: { days: number; label: string }[] = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

/** Turn a relative `/share/:token` path into an absolute URL the broker
 *  can paste anywhere. Already-absolute values pass through untouched. */
export function absolutePublicShareUrl(path: string): string {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  if (typeof window !== "undefined") return `${window.location.origin}${path}`;
  return path;
}

/** Best-effort clipboard copy with the legacy execCommand fallback for
 *  insecure-origin / older-browser cases. */
export function copyShareUrl(value: string) {
  if (!value) return;
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(value).catch(() => legacyCopy(value));
    return;
  }
  legacyCopy(value);
}

function legacyCopy(value: string) {
  const el = document.createElement("textarea");
  el.value = value;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand("copy");
  } catch {
    /* swallow */
  }
  document.body.removeChild(el);
}

/**
 * State + actions for the agency share-link flow. Owns the list of the
 * caller's own active links, the just-generated link modal, and polling
 * the modal-tied link until photo rehosting finishes.
 */
export function useShareLinks() {
  const [shareLinks, setShareLinks] = useState<ClientShareLink[] | null>(null);
  const [shareModal, setShareModal] = useState<ClientShareLink | null>(null);
  const [generating, setGenerating] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const loadShareLinks = useCallback(async () => {
    try {
      const r = await api<{ items: ClientShareLink[] }>("/api/share-links");
      setShareLinks(r.items);
    } catch {
      setShareLinks([]);
    }
  }, []);

  useEffect(() => {
    loadShareLinks();
  }, [loadShareLinks]);

  // Poll the modal-tied link until photo rehosting completes (same 2.5s
  // cadence as the Market flow).
  useEffect(() => {
    if (!shareModal || shareModal.status !== "preparing") return;
    const token = shareModal.token;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api<{ items: ClientShareLink[] }>("/api/share-links");
        const fresh = r.items.find((l) => l.token === token);
        if (!cancelled && fresh) {
          setShareModal(fresh);
          setShareLinks(r.items);
        }
      } catch {
        /* swallow; next tick retries */
      }
    };
    const id = window.setInterval(tick, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [shareModal]);

  const generate = useCallback(
    async (
      selections: ShareSelection[],
      opts: {
        agentPhone?: string;
        language: ShareLang;
        lifetimeDays?: number;
        /** The broker's own pasted MyHome/SS listing URLs, added to the same
         *  brochure as `kind="external"` items. */
        urls?: string[];
      },
    ): Promise<boolean> => {
      const urls = (opts.urls ?? []).map((u) => u.trim()).filter(Boolean);
      const total = selections.length + urls.length;
      if (total === 0) {
        setShareError("Pick a listing or paste a URL first.");
        return false;
      }
      if (total > SHARE_MAX_SELECTIONS) {
        setShareError(`Up to ${SHARE_MAX_SELECTIONS} listings per share link.`);
        return false;
      }
      setGenerating(true);
      setShareError(null);
      try {
        const link = await api<ClientShareLink>("/api/share-links", {
          method: "POST",
          json: {
            selections,
            urls,
            agent_phone: opts.agentPhone?.trim() || null,
            language: opts.language,
            lifetime_days: opts.lifetimeDays ?? 30,
          },
        });
        setShareModal(link);
        await loadShareLinks();
        return true;
      } catch (err) {
        setShareError(
          err instanceof Error ? err.message : "Failed to generate public URL",
        );
        return false;
      } finally {
        setGenerating(false);
      }
    },
    [loadShareLinks],
  );

  const renew = useCallback(
    async (linkId: string, lifetimeDays = 30) => {
      try {
        await api(`/api/share-links/${linkId}/renew`, {
          method: "POST",
          json: { lifetime_days: lifetimeDays },
        });
        await loadShareLinks();
      } catch (err) {
        setShareError(err instanceof Error ? err.message : "Renew failed");
      }
    },
    [loadShareLinks],
  );

  const revoke = useCallback(
    async (linkId: string) => {
      if (
        !window.confirm(
          "Revoke this public URL? It will stop working immediately.",
        )
      )
        return;
      try {
        await api(`/api/share-links/${linkId}`, { method: "DELETE" });
        await loadShareLinks();
      } catch (err) {
        setShareError(err instanceof Error ? err.message : "Revoke failed");
      }
    },
    [loadShareLinks],
  );

  return {
    shareLinks,
    shareModal,
    setShareModal,
    generating,
    shareError,
    setShareError,
    loadShareLinks,
    generate,
    renew,
    revoke,
  };
}

/** Small language flag chip used in lists. */
function langLabel(lang: string): string {
  return lang === "ru" ? "🇷🇺 RU" : lang === "en" ? "🇬🇧 EN" : "🇬🇪 GE";
}

/** Inline radio group for the public-page language. */
export function ShareLangPicker({
  value,
  onChange,
  disabled,
}: {
  value: ShareLang;
  onChange: (l: ShareLang) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex items-center gap-1.5 text-xs">
      <span className="text-slate-500">Language:</span>
      <div
        role="radiogroup"
        aria-label="Public page language"
        className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5"
      >
        {SHARE_LANGS.map(({ code, label, flag }) => {
          const active = value === code;
          return (
            <button
              key={code}
              type="button"
              role="radio"
              aria-checked={active}
              disabled={disabled}
              onClick={() => onChange(code)}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold leading-none transition ${
                active ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-200"
              } disabled:opacity-50`}
            >
              <span aria-hidden>{flag}</span>
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Active links panel ──────────────────────────────────────────────────────

function relTime(iso: string | null): string | null {
  if (!iso) return null;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d === 1 ? "yesterday" : `${d}d ago`;
}

/** "expires in 23d" / "expires today" plus a tone that warms up near the end. */
function expiryLabel(iso: string): { text: string; tone: string } {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days <= 0) return { text: "expires today", tone: "text-rose-300" };
  if (days === 1) return { text: "expires tomorrow", tone: "text-amber-300" };
  return {
    text: `expires in ${days}d`,
    tone: days <= 3 ? "text-amber-300" : "text-slate-500",
  };
}

function priceLabel(item: ClientShareLink["items"][number]): string | null {
  const usd = item.price_usd != null ? Number(item.price_usd) : NaN;
  if (Number.isFinite(usd) && usd > 0) return `$${Math.round(usd).toLocaleString()}`;
  const native = item.price != null ? Number(item.price) : NaN;
  if (Number.isFinite(native) && native > 0) {
    const sym = (item.currency || "").toUpperCase() === "GEL" ? "₾" : "";
    return `${sym}${Math.round(native).toLocaleString()}`;
  }
  return null;
}

function StatusPill({ status }: { status: ClientShareLink["status"] }) {
  const cls =
    status === "ready"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
      : status === "preparing"
        ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
        : "border-rose-500/30 bg-rose-500/10 text-rose-300";
  const dot =
    status === "ready"
      ? "bg-emerald-400"
      : status === "preparing"
        ? "bg-amber-400 animate-pulse"
        : "bg-rose-400";
  const label = status === "ready" ? "Ready" : status === "preparing" ? "Preparing…" : "Failed";
  return (
    <span className={`badge whitespace-nowrap ${cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

/** Up to three overlapping cover thumbnails; a placeholder tile while the
 *  prep worker is still fetching photos. */
function ThumbStack({ items }: { items: ClientShareLink["items"] }) {
  const covers = items.map((i) => i.cover_photo).filter((u): u is string => !!u).slice(0, 3);
  if (!covers.length) {
    return (
      <div className="h-12 w-12 flex-shrink-0 rounded-lg border border-white/10 bg-ink-900 flex items-center justify-center text-slate-600">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="m21 15-4.5-4.5L8 19" />
        </svg>
      </div>
    );
  }
  return (
    <div className="flex-shrink-0 flex items-center" style={{ width: `${48 + (covers.length - 1) * 18}px` }}>
      {covers.map((u, idx) => (
        <img
          key={u + idx}
          src={u}
          alt=""
          loading="lazy"
          className="h-12 w-12 rounded-lg object-cover border-2 border-ink-900 bg-ink-900 shadow-sm"
          style={{ marginLeft: idx === 0 ? 0 : -30, zIndex: covers.length - idx, position: "relative" }}
        />
      ))}
    </div>
  );
}

const LINK_ACTION =
  "inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11.5px] font-medium whitespace-nowrap transition-colors";

/** Card grid of the caller's active public links — cover thumbnails, status,
 *  copyable URL, buyer analytics, and the per-card listing breakdown. Also
 *  mounted on Placements (read-only when ``onEdit`` is omitted). */
export function ActivePublicLinksPanel({
  links,
  onRevoke,
  onRenew,
  onEdit,
  embedded = false,
}: {
  links: ClientShareLink[];
  onRevoke: (id: string) => void;
  onRenew?: (id: string) => void;
  /** Opens the edit modal. Omit to render the panel read-only. */
  onEdit?: (id: string) => void;
  /** True inside a dialog: no card chrome / title, and an empty state instead
   *  of rendering nothing. */
  embedded?: boolean;
}) {
  if (embedded) {
    if (!links.length) {
      return (
        <div className="text-[12.5px] text-slate-500 py-8 text-center rounded-xl border border-dashed border-white/10">
          No active links yet — pick listings with "Prepare to share", or paste portal URLs
          with "From URLs", to build a public brochure for a client.
        </div>
      );
    }
    return (
      <div className="grid gap-2.5 lg:grid-cols-2">
        {links.map((link) => (
          <ShareLinkCard key={link.id} link={link} onRevoke={onRevoke} onRenew={onRenew} onEdit={onEdit} />
        ))}
      </div>
    );
  }
  if (!links.length) return null;
  return (
    <div className="card p-3 mb-5">
      <div className="flex items-center gap-2 mb-2.5">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          Active public links
        </div>
        <span className="badge border-white/10 bg-white/[0.04] text-slate-300 tabular-nums">
          {links.length}
        </span>
      </div>
      <div className="grid gap-2.5 lg:grid-cols-2">
        {links.map((link) => (
          <ShareLinkCard
            key={link.id}
            link={link}
            onRevoke={onRevoke}
            onRenew={onRenew}
            onEdit={onEdit}
          />
        ))}
      </div>
    </div>
  );
}

function ShareLinkCard({
  link,
  onRevoke,
  onRenew,
  onEdit,
}: {
  link: ClientShareLink;
  onRevoke: (id: string) => void;
  onRenew?: (id: string) => void;
  onEdit?: (id: string) => void;
}) {
  const [showItems, setShowItems] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = absolutePublicShareUrl(link.public_url);
  const display = url.replace(/^https?:\/\//, "");
  const items = [...link.items].sort((a, b) => a.order - b.order);
  const titled = items.find((i) => i.title);
  const headline = titled?.title || `${link.item_count} listing${link.item_count === 1 ? "" : "s"}`;
  const extra = titled ? link.item_count - 1 : 0;
  const expiry = expiryLabel(link.expires_at);
  const created = relTime(link.created_at);
  const opened = items.filter((i) => i.open_count > 0);

  function copy() {
    copyShareUrl(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="rounded-xl border border-white/[0.07] bg-ink-850/60 p-3 flex flex-col gap-2.5 min-w-0">
      {/* Headline: thumbs · title + meta · status */}
      <div className="flex items-start gap-3 min-w-0">
        <ThumbStack items={items} />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-slate-100 truncate" title={headline}>
            {headline}
            {extra > 0 && (
              <span className="font-normal text-slate-500"> +{extra} more</span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 truncate mt-0.5">
            {link.item_count} listing{link.item_count === 1 ? "" : "s"} · {langLabel(link.language)}
            {created ? ` · created ${created}` : ""}
          </div>
        </div>
        <StatusPill status={link.status} />
      </div>

      {/* URL row */}
      <div className="flex items-center gap-1 h-9 rounded-lg border border-white/[0.06] bg-ink-950/60 pl-2.5 pr-1 min-w-0">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500 flex-shrink-0">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-0 truncate font-mono text-[11.5px] text-accent-400 hover:text-slate-100"
          title={url}
        >
          {display}
        </a>
        <button
          type="button"
          onClick={copy}
          className={`${LINK_ACTION} ${copied ? "text-emerald-300" : "text-slate-300 hover:text-slate-100 hover:bg-white/[0.06]"}`}
          title="Copy link"
        >
          {copied ? (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
          ) : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
          )}
          {copied ? "Copied" : "Copy"}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`${LINK_ACTION} text-slate-300 hover:text-slate-100 hover:bg-white/[0.06]`}
          title="Open the public page"
        >
          Open
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M8 7h9v9" /></svg>
        </a>
      </div>

      {/* Analytics + expiry */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] min-w-0">
        <span className={`inline-flex items-center gap-1 ${link.view_count ? "text-emerald-300/90" : "text-slate-500"}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>
          {link.view_count
            ? `viewed ${link.view_count}×${link.last_viewed_at ? ` · last ${relTime(link.last_viewed_at)}` : ""}`
            : "not viewed yet"}
        </span>
        {opened.length > 0 && (
          <span className="text-slate-400 truncate min-w-0">
            opened {opened.map((i) => i.title || "a listing").join(", ")}
          </span>
        )}
        <span className={`inline-flex items-center gap-1 ml-auto ${expiry.tone}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
          {expiry.text}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 pt-2 border-t border-white/[0.05]">
        <button
          type="button"
          onClick={() => setShowItems((v) => !v)}
          aria-expanded={showItems}
          className={`${LINK_ACTION} -ml-2 text-slate-400 hover:text-slate-100 hover:bg-white/[0.06]`}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${showItems ? "rotate-90" : ""}`}><path d="m9 6 6 6-6 6" /></svg>
          Listings ({items.length})
        </button>
        <div className="ml-auto flex items-center gap-1">
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(link.id)}
              title="Add or remove listings, reorder them, edit details — the URL stays the same"
              className={`${LINK_ACTION} text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100`}
            >
              Edit
            </button>
          )}
          {onRenew && (
            <button
              type="button"
              onClick={() => onRenew(link.id)}
              title="Extend the link's lifetime by 30 days"
              className={`${LINK_ACTION} text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100`}
            >
              Renew
            </button>
          )}
          <button
            type="button"
            onClick={() => onRevoke(link.id)}
            className={`${LINK_ACTION} text-rose-300/80 hover:text-rose-200 hover:bg-rose-500/10`}
          >
            Revoke
          </button>
        </div>
      </div>

      {/* Per-listing breakdown */}
      {showItems && (
        <ol className="space-y-1.5 -mt-1">
          {items.map((item, idx) => {
            const price = priceLabel(item);
            const facts = [
              price,
              item.rooms ? `${item.rooms} rm` : null,
              item.area_sqm ? `${Math.round(Number(item.area_sqm))} m²` : null,
              item.location,
            ].filter(Boolean);
            return (
              <li key={item.id} className="flex items-center gap-2.5 rounded-lg bg-white/[0.025] px-2 py-1.5 min-w-0">
                <span className="text-[10.5px] text-slate-600 tabular-nums w-3.5 text-right flex-shrink-0">{idx + 1}</span>
                {item.cover_photo ? (
                  <img src={item.cover_photo} alt="" loading="lazy" className="h-9 w-9 rounded-md object-cover flex-shrink-0 bg-ink-900" />
                ) : (
                  <span className="h-9 w-9 rounded-md border border-white/10 bg-ink-900 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] text-slate-200 truncate">
                    {item.title || (item.status === "preparing" ? "Preparing…" : item.status === "failed" ? "Couldn't fetch this listing" : "Untitled listing")}
                  </div>
                  <div className="text-[10.5px] text-slate-500 truncate">
                    {facts.length ? facts.join(" · ") : item.status}
                    {item.open_count > 0 && (
                      <span className="text-emerald-300/80"> · opened {item.open_count}×</span>
                    )}
                  </div>
                </div>
                {item.source_url && (
                  <a
                    href={item.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={item.source_url}
                    className="text-[10.5px] text-slate-500 hover:text-accent-400 whitespace-nowrap flex-shrink-0"
                  >
                    {item.platform === "myhome" || /myhome/.test(item.source_url) ? "MyHome" : /ss\.ge/.test(item.source_url) ? "ss.ge" : "source"} ↗
                  </a>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

/** Toolbar button that opens {@link SharePrepareModal} in URL-only mode.
 *  Styled to match the other toolbar buttons on Fresh / Placements. */
export function ShareFromUrlsButton({
  onClick,
  className = "",
}: {
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Paste MyHome / ss.ge listing URLs and get the same public brochure link"
      className={`h-9 px-3 rounded-lg text-[12px] font-medium text-slate-300 border border-white/10 hover:border-white/20 hover:text-slate-100 transition whitespace-nowrap ${className}`}
    >
      🔗 From URLs
    </button>
  );
}

/** "Prepare to share" dialog — collects the optional contact phone and the
 *  public-page language, then generates the link for the current selection.
 *
 *  With `urlsOnly` there is no selection at all: the broker pastes MyHome /
 *  ss.ge URLs they already have in hand and gets the same public page. Both
 *  modes post to the same `/api/share-links` endpoint, so a link built from
 *  URLs is indistinguishable from a selection-built one afterwards. */
export function SharePrepareModal({
  count,
  generating,
  error,
  urlsOnly = false,
  onGenerate,
  onClose,
}: {
  count: number;
  generating: boolean;
  error: string | null;
  /** Hide the selection copy and lead with the URL paste box. */
  urlsOnly?: boolean;
  onGenerate: (opts: {
    agentPhone: string;
    language: ShareLang;
    lifetimeDays: number;
    urls: string[];
  }) => void;
  onClose: () => void;
}) {
  const [agentPhone, setAgentPhone] = useState("");
  const [lang, setLang] = useState<ShareLang>("ka");
  const [lifetimeDays, setLifetimeDays] = useState(30);
  const [urlsText, setUrlsText] = useState("");
  const urls = urlsText.split(/\s+/).map((s) => s.trim()).filter(Boolean);
  const total = count + urls.length;
  const tooMany = total > SHARE_MAX_SELECTIONS;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !generating && onClose()}
    >
      <div
        className="card max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-lg font-semibold text-slate-100">
            {urlsOnly ? "Public page from URLs" : "Prepare a public page"}
          </div>
          <p className="text-sm text-slate-400 mt-1">
            {urlsOnly ? (
              <>
                Paste the MyHome / ss.ge listing URLs you already have and get
                the same polished public page, with your contact on every card.
                Up to {SHARE_MAX_SELECTIONS} listings per link.
              </>
            ) : (
              <>
                Turn your selection{" "}
                {count > 0 && (
                  <>
                    (
                    <span className="text-accent-300 font-semibold tabular-nums">
                      {count}
                    </span>
                    ){" "}
                  </>
                )}
                — and any of your own MyHome / ss.ge listing URLs — into a
                polished public page with your contact on every card. Up to{" "}
                {SHARE_MAX_SELECTIONS} listings per link.
              </>
            )}
          </p>
        </div>

        <div className="space-y-3">
          <input
            type="tel"
            value={agentPhone}
            onChange={(e) => setAgentPhone(e.target.value)}
            placeholder="Your phone for the contact card (optional)"
            className="input text-sm w-full"
            maxLength={32}
            disabled={generating}
          />
          <div>
            <label className="label">
              {urlsOnly ? "Listing URLs" : "Add your own listings (optional)"}
            </label>
            <textarea
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              placeholder="Paste MyHome or ss.ge listing URLs — one per line"
              rows={urlsOnly ? 6 : 3}
              autoFocus={urlsOnly}
              className="input text-sm w-full resize-y font-mono !text-[12px]"
              disabled={generating}
            />
            <p className="mt-1 text-[11px] text-slate-500">
              {urls.length > 0
                ? `${urls.length} URL${urls.length === 1 ? "" : "s"} · ${total}/${SHARE_MAX_SELECTIONS} total`
                : urlsOnly
                  ? `Each URL becomes a brochure card — photos, price and your contact. Up to ${SHARE_MAX_SELECTIONS}.`
                  : "Your own listings get the same brochure card — photos, price and your contact."}
            </p>
          </div>
          <ShareLangPicker value={lang} onChange={setLang} disabled={generating} />
          <div className="inline-flex items-center gap-1.5 text-xs">
            <span className="text-slate-500">Expires in:</span>
            <div
              role="radiogroup"
              aria-label="Link lifetime"
              className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5"
            >
              {SHARE_LIFETIMES.map(({ days, label }) => {
                const active = lifetimeDays === days;
                return (
                  <button
                    key={days}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={generating}
                    onClick={() => setLifetimeDays(days)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold leading-none transition ${
                      active ? "bg-white/15 text-white" : "text-slate-400 hover:text-slate-200"
                    } disabled:opacity-50`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {tooMany && (
          <div className="text-xs text-rose-300">
            Too many — up to {SHARE_MAX_SELECTIONS} listings per link.
          </div>
        )}
        {error && <div className="text-xs text-rose-300">{error}</div>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={generating}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() =>
              onGenerate({ agentPhone, language: lang, lifetimeDays, urls })
            }
            disabled={generating || total === 0 || tooMany}
          >
            {generating ? "Generating…" : "🔗 Generate public URL"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Post-generation modal: surfaces the ready-to-copy URL and the photo
 *  rehosting progress. */
export function ShareLinkModal({
  link,
  onClose,
}: {
  link: ClientShareLink;
  onClose: () => void;
}) {
  const url = absolutePublicShareUrl(link.public_url);
  const preparing = link.status === "preparing";
  const failed = link.status === "failed";
  const expires = new Date(link.expires_at).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
  const readyCount = link.items.filter((i) => i.status === "ready").length;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-lg font-semibold text-slate-100">
            Public URL ready to share
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Send this link to your lead. They'll see the listings you picked
            with your contact details on every card. The link expires {expires}.
          </p>
        </div>
        <div className="flex items-stretch gap-2">
          <input
            type="text"
            readOnly
            value={url}
            onClick={(e) => (e.target as HTMLInputElement).select()}
            className="input flex-1 mono text-xs"
          />
          <button type="button" className="btn-primary" onClick={() => copyShareUrl(url)}>
            Copy
          </button>
        </div>
        {preparing && (
          <div className="text-xs text-amber-300 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            Photos still rehosting in the background — the link works the moment
            it flips to ready ({readyCount}/{link.items.length} items rehosted).
          </div>
        )}
        {failed && (
          <div className="text-xs text-rose-300">
            Photo rehosting failed for every listing. Try generating again or
            pick different ones.
          </div>
        )}
        {!preparing && !failed && (
          <div className="text-xs text-emerald-300">
            ✓ Ready — all {link.item_count} listings are live on the public page.
          </div>
        )}
        <div className="flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Editing an already-shared link ──────────────────────────────────────
 *
 * The whole point is that the URL doesn't change: the broker has already
 * sent it to the lead, so adding the listing they asked about, dropping the
 * one they rejected, or fixing a price happens in place on the live page.
 * Every action writes through immediately and re-renders from the server's
 * response — there is no local draft to get out of sync with what the buyer
 * is looking at right now.
 */

/** Editable snapshot fields, grouped the way a broker thinks about a card. */
const ITEM_TEXT_FIELDS: { key: keyof ShareItemSnapshot; label: string }[] = [
  { key: "title", label: "Title" },
  { key: "district", label: "District" },
  { key: "neighborhood", label: "Neighborhood" },
  { key: "street", label: "Street" },
  { key: "property_type", label: "Property type" },
  { key: "deal_type", label: "Deal type" },
  { key: "building_status", label: "Building status" },
  { key: "condition", label: "Condition" },
];
const ITEM_NUM_FIELDS: { key: keyof ShareItemSnapshot; label: string }[] = [
  { key: "price", label: "Price" },
  { key: "currency", label: "Currency" },
  { key: "area_sqm", label: "Area m²" },
  { key: "rooms", label: "Rooms" },
  { key: "bedrooms", label: "Bedrooms" },
  { key: "bathrooms", label: "Bathrooms" },
  { key: "floor", label: "Floor" },
  { key: "total_floors", label: "Floors total" },
  { key: "year_built", label: "Year built" },
];
/** Fields the API takes as `int | null` — blank must send null, not "". */
const ITEM_INT_KEYS = new Set<keyof ShareItemSnapshot>([
  "rooms",
  "bedrooms",
  "bathrooms",
  "floor",
  "total_floors",
  "year_built",
]);

/** Inline per-item field editor. Writes through the same endpoint the public
 *  page's owner-only editor uses, so a fix made here and one made there are
 *  the same operation. */
function ShareItemFieldsEditor({
  item,
  onSaved,
}: {
  item: ClientShareLinkDetail["items"][number];
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<ShareItemSnapshot>(() => ({ ...item.snapshot }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set = (key: keyof ShareItemSnapshot, value: string) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const json: Record<string, unknown> = {};
      for (const { key } of [...ITEM_TEXT_FIELDS, ...ITEM_NUM_FIELDS]) {
        const raw = draft[key];
        const str = raw === null || raw === undefined ? "" : String(raw);
        if (ITEM_INT_KEYS.has(key)) {
          json[key] = str.trim() === "" ? null : Number(str);
        } else {
          json[key] = str;
        }
      }
      json.full_description = draft.full_description ?? "";
      await api(`/api/share-links/items/${item.id}`, { method: "PATCH", json });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[...ITEM_NUM_FIELDS, ...ITEM_TEXT_FIELDS].map(({ key, label }) => (
          <label key={String(key)} className="block">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500">
              {label}
            </span>
            <input
              type="text"
              value={draft[key] === null || draft[key] === undefined ? "" : String(draft[key])}
              onChange={(e) => set(key, e.target.value)}
              className="input text-xs w-full mt-0.5"
              disabled={saving}
            />
          </label>
        ))}
      </div>
      <label className="block">
        <span className="block text-[10px] uppercase tracking-wider text-slate-500">
          Description
        </span>
        <textarea
          value={draft.full_description ?? ""}
          onChange={(e) =>
            setDraft((d) => ({ ...d, full_description: e.target.value }))
          }
          rows={4}
          className="input text-xs w-full mt-0.5 resize-y"
          disabled={saving}
        />
      </label>
      {err && <div className="text-[11px] text-rose-300">{err}</div>}
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={save}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save card"}
        </button>
      </div>
    </div>
  );
}

/** Full edit surface for one link: settings, the ordered listing list, and
 *  an add-by-URL box. Loads its own detail payload so the caller only has to
 *  hand it a link id. */
export function ShareEditModal({
  linkId,
  onClose,
  onChanged,
}: {
  linkId: string;
  onClose: () => void;
  /** Called after every successful write so the caller can refresh its list. */
  onChanged?: () => void;
}) {
  const [link, setLink] = useState<ClientShareLinkDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [addText, setAddText] = useState("");

  // Settings draft — only these three are free text, so a local draft that
  // commits on "Save" is friendlier than a write per keystroke.
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  const apply = useCallback(
    (fresh: ClientShareLinkDetail) => {
      setLink(fresh);
      setPhone(fresh.agent_phone ?? "");
      setFullName(fresh.agent_full_name ?? "");
      setEmail(fresh.agent_email ?? "");
      onChanged?.();
    },
    [onChanged],
  );

  const load = useCallback(async () => {
    try {
      apply(await api<ClientShareLinkDetail>(`/api/share-links/${linkId}`));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not load this link");
    }
  }, [linkId, apply]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll while cards are still scraping so newly added listings fill in
  // without the broker having to reopen the modal.
  useEffect(() => {
    if (!link || link.status !== "preparing") return;
    const id = window.setInterval(load, 2500);
    return () => window.clearInterval(id);
  }, [link, load]);

  /** Run a write, swap in the link it returns, surface any error. */
  const write = useCallback(
    async (path: string, init: Parameters<typeof api>[1]) => {
      setBusy(true);
      setErr(null);
      try {
        apply(await api<ClientShareLinkDetail>(path, init));
        return true;
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Something went wrong");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [apply],
  );

  const move = (index: number, delta: number) => {
    if (!link) return;
    const ids = link.items.map((i) => i.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    write(`/api/share-links/${linkId}/items/reorder`, {
      method: "POST",
      json: { item_ids: ids },
    });
  };

  const addUrls = link ? addText.split(/\s+/).map((s) => s.trim()).filter(Boolean) : [];
  const remaining = link ? SHARE_MAX_SELECTIONS - link.items.length : 0;
  const url = link ? absolutePublicShareUrl(link.public_url) : "";

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={() => !busy && onClose()}
    >
      <div
        className="card max-w-3xl w-full p-6 space-y-5 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-lg font-semibold text-slate-100">
            Edit public page
          </div>
          <p className="text-sm text-slate-400 mt-1">
            Changes go live immediately on the same URL — no need to send your
            lead a new link.
          </p>
          {url && (
            <div className="mt-2 flex items-stretch gap-2">
              <input
                type="text"
                readOnly
                value={url}
                onClick={(e) => (e.target as HTMLInputElement).select()}
                className="input flex-1 mono text-xs"
              />
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => copyShareUrl(url)}
              >
                Copy
              </button>
            </div>
          )}
        </div>

        {err && <div className="text-xs text-rose-300">{err}</div>}
        {!link && !err && <div className="text-sm text-slate-400">Loading…</div>}

        {link && (
          <>
            {/* ── settings ────────────────────────────────────────────── */}
            <section className="space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-accent-400">
                Page settings
              </h4>
              <div className="flex flex-wrap items-center gap-4">
                <ShareLangPicker
                  value={link.language}
                  onChange={(l) =>
                    write(`/api/share-links/${linkId}`, {
                      method: "PATCH",
                      json: { language: l },
                    })
                  }
                  disabled={busy}
                />
                <div className="inline-flex items-center gap-1.5 text-xs">
                  <span className="text-slate-500">Extend to:</span>
                  <div className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5">
                    {SHARE_LIFETIMES.map(({ days, label }) => (
                      <button
                        key={days}
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          write(`/api/share-links/${linkId}`, {
                            method: "PATCH",
                            json: { lifetime_days: days },
                          })
                        }
                        className="px-2.5 py-1 rounded-full text-xs font-semibold leading-none text-slate-400 hover:text-slate-200 transition disabled:opacity-50"
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <span className="text-[11px] text-slate-500">
                  expires{" "}
                  {new Date(link.expires_at).toLocaleDateString(undefined, {
                    dateStyle: "medium",
                  })}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Name on the contact card"
                  className="input text-sm w-full"
                  maxLength={200}
                  disabled={busy}
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone"
                  className="input text-sm w-full"
                  maxLength={32}
                  disabled={busy}
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="input text-sm w-full"
                  maxLength={200}
                  disabled={busy}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="btn-secondary text-xs"
                  disabled={busy}
                  onClick={() =>
                    write(`/api/share-links/${linkId}`, {
                      method: "PATCH",
                      json: {
                        agent_full_name: fullName,
                        agent_phone: phone,
                        agent_email: email,
                      },
                    })
                  }
                >
                  Save contact details
                </button>
              </div>
            </section>

            {/* ── listings ────────────────────────────────────────────── */}
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-accent-400">
                Listings{" "}
                <span className="text-slate-500 font-normal normal-case tracking-normal">
                  {link.items.length}/{SHARE_MAX_SELECTIONS}
                </span>
              </h4>
              <ul className="divide-y divide-white/5">
                {link.items.map((item, idx) => {
                  const tone =
                    item.status === "ready"
                      ? "text-emerald-300"
                      : item.status === "preparing"
                        ? "text-amber-300"
                        : "text-rose-300";
                  return (
                    <li key={item.id} className="py-2">
                      <div className="flex items-start gap-2">
                        <div className="flex flex-col leading-none pt-0.5">
                          <button
                            type="button"
                            className="text-slate-500 hover:text-accent-300 disabled:opacity-25 text-xs"
                            onClick={() => move(idx, -1)}
                            disabled={busy || idx === 0}
                            aria-label="Move up"
                          >
                            ▲
                          </button>
                          <button
                            type="button"
                            className="text-slate-500 hover:text-accent-300 disabled:opacity-25 text-xs"
                            onClick={() => move(idx, 1)}
                            disabled={busy || idx === link.items.length - 1}
                            aria-label="Move down"
                          >
                            ▼
                          </button>
                        </div>
                        <span className="text-xs text-slate-600 tabular-nums pt-0.5 w-4">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-200 truncate">
                            {item.title || item.source_url || "Untitled listing"}
                          </div>
                          {item.source_url && (
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] mono text-accent-300 hover:text-accent-200 break-all block mt-0.5"
                              title="Open the portal listing this card was built from"
                            >
                              {item.source_url} ↗
                            </a>
                          )}
                          <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                            <span className={tone}>{item.status}</span>
                            <span>·</span>
                            <span>{item.kind}</span>
                            <span>·</span>
                            <span>{item.photo_count} photos</span>
                            {item.open_count > 0 && (
                              <>
                                <span>·</span>
                                <span className="text-emerald-300/90">
                                  opened {item.open_count}×
                                </span>
                              </>
                            )}
                          </div>
                          {item.error_message && (
                            <div className="text-[11px] text-rose-300 mt-0.5">
                              {item.error_message}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-xs text-slate-500 hover:text-accent-300"
                            onClick={() =>
                              setExpanded((c) => (c === item.id ? null : item.id))
                            }
                          >
                            {expanded === item.id ? "Close" : "Edit"}
                          </button>
                          <button
                            type="button"
                            className="text-xs text-slate-500 hover:text-rose-300 disabled:opacity-30"
                            disabled={busy || link.items.length <= 1}
                            title={
                              link.items.length <= 1
                                ? "A public page needs at least one listing — revoke the link instead"
                                : "Remove this listing from the page"
                            }
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Remove this listing from the public page?",
                                )
                              )
                                write(
                                  `/api/share-links/${linkId}/items/${item.id}`,
                                  { method: "DELETE" },
                                );
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      {expanded === item.id && (
                        <ShareItemFieldsEditor
                          item={item}
                          onSaved={() => {
                            setExpanded(null);
                            load();
                          }}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* ── add ─────────────────────────────────────────────────── */}
            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-accent-400">
                Add listings
              </h4>
              <textarea
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                placeholder="Paste MyHome or ss.ge listing URLs — one per line"
                rows={2}
                className="input text-sm w-full resize-y font-mono !text-[12px]"
                disabled={busy || remaining <= 0}
              />
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-500">
                  {remaining > 0
                    ? `Room for ${remaining} more listing${remaining === 1 ? "" : "s"}.`
                    : `This page is full — remove a listing to add another.`}
                </p>
                <button
                  type="button"
                  className="btn-primary text-xs"
                  disabled={
                    busy ||
                    addUrls.length === 0 ||
                    addUrls.length > remaining
                  }
                  onClick={async () => {
                    const okd = await write(
                      `/api/share-links/${linkId}/items`,
                      { method: "POST", json: { urls: addUrls } },
                    );
                    if (okd) setAddText("");
                  }}
                >
                  {busy ? "Working…" : "Add to page"}
                </button>
              </div>
              {addUrls.length > remaining && remaining > 0 && (
                <div className="text-[11px] text-rose-300">
                  That's {addUrls.length} URLs but only {remaining} slot
                  {remaining === 1 ? "" : "s"} left.
                </div>
              )}
              {link.status === "preparing" && (
                <div className="text-xs text-amber-300 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  Scraping the new listings — they appear on the page as they
                  finish.
                </div>
              )}
            </section>
          </>
        )}

        <div className="flex justify-end">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
